import React, { FC, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import { AnyEntityId, EntityTypeSupportingFiles } from "@mapmycustomers/shared/types/entity/types";
import File, { RawFile } from "@mapmycustomers/shared/types/File";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import PdfFilePreview from "@app/component/FilePreview/PdfFilePreview";
import { fetchFile } from "@app/store/files/actions";
import { downloadFile } from "@app/util/file";
import { formatRawDate } from "@app/util/formatters";
import hasMimeType, { DOCUMENT_PDF, IMAGE_ANY, VIDEO_ANY } from "@app/util/mimeType";

import ActionButtons from "./components/ActionButtons";
import styles from "./FilePreviewPage.module.scss";
import useDragging from "./utils/useDragging";

const messages = defineMessages({
  back: {
    id: "files.filePreviewPage.back",
    defaultMessage: "Back",
    description: "Back button text on the File Preview page",
  },
  noPreview: {
    id: "files.filePreviewPage.noPreview",
    defaultMessage: "No preview available",
    description: "No preview text on the File Preview page",
  },
  notFound: {
    id: "files.filePreviewPage.notFound",
    defaultMessage: "File not found",
    description: "File not found error on the File Preview page",
  },
  uploaded: {
    id: "files.filePreviewPage.info.dateCreated",
    defaultMessage: "Uploaded {date}",
    description: "Date file was uploaded on the File Preview page",
  },
  uploadedDateFormat: {
    id: "files.filePreviewPage.info.dateCreated.format",
    defaultMessage: "'on' PP 'at' p",
    description: "Date format for the Uploaded info File Preview page",
  },
});

export interface FilePreviewProps {
  entityId?: AnyEntityId;
  entityType?: EntityTypeSupportingFiles;
  fileId: File["id"];
}

interface Props extends FilePreviewProps {
  onClose?: () => void;
  onFetchFile: typeof fetchFile;
}

const FilePreviewPage: FC<Props> = ({ entityId, entityType, fileId, onClose, onFetchFile }) => {
  const intl = useIntl();

  const [loading, setLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | undefined>();
  const [blob, setBlob] = useState<Blob | undefined>();

  const isImageFile = file ? hasMimeType(file, [...IMAGE_ANY]) : false;
  const isPdf = file ? hasMimeType(file, [...DOCUMENT_PDF]) : false;
  const isVideo = file ? hasMimeType(file, [...VIDEO_ANY]) : false;

  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    setLoading(true);
    onFetchFile({
      entityId,
      entityType,
      fileId,
      onFailure: () => {
        setLoading(false);
        // also, "not found" notification is prevented when onFailure callback is present
      },
      onSuccess: (file: RawFile, blob: Blob) => {
        setFile(file);
        setBlob(blob);
        setLoading(false);
      },
    });
  }, [fileId, entityId, entityType, onFetchFile]);

  const handleDownload = useCallback(() => {
    if (blob && file) {
      downloadFile(file.name, blob);
    }
  }, [blob, file]);

  const [srcUrl, setSrcUrl] = useState<string | undefined>();
  useEffect(() => {
    if (!blob) {
      return;
    }

    const newUrl = URL.createObjectURL(blob);
    setSrcUrl((oldSrc) => {
      if (oldSrc) {
        URL.revokeObjectURL(oldSrc);
      }
      return newUrl;
    });
    return () => {
      URL.revokeObjectURL(newUrl);
    };
  }, [blob]);

  const [previewRef, setPreviewRef] = useState<HTMLDivElement | null>(null);
  const [dragState, handleMouseDown, handleMouseUp, handleMouseMove, handleMouseLeave] =
    useDragging(previewRef);

  const [isPreviewScrollable, setPreviewScrollable] = useState(false);
  useEffect(() => {
    // Can't use useMemo here because scale should change first and only
    // then we can check if preview container became scrollable.
    // If for any reason this stops working, use MutationObserver.
    setPreviewScrollable(
      zoom > 1 &&
        !!previewRef &&
        (previewRef.scrollWidth > previewRef.clientWidth ||
          previewRef.scrollHeight > previewRef.clientHeight)
    );
  }, [previewRef, zoom]);

  return (
    <div className={styles.container}>
      {onClose && (
        <div>
          <Button leftIcon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={onClose} size="small">
            {intl.formatMessage(messages.back)}
          </Button>
        </div>
      )}
      <div className={styles.header}>
        <div className={styles.fileInfo}>
          <div className={styles.name}>{file?.name}</div>
          <div className={styles.dateCreated}>
            {file
              ? intl.formatMessage(messages.uploaded, {
                  date: formatRawDate(
                    file.createdAt,
                    intl.formatMessage(messages.uploadedDateFormat)
                  ),
                })
              : null}
          </div>
        </div>
        <ActionButtons
          canDownload={!!file}
          isImageFile={isImageFile}
          onDownload={handleDownload}
          onZoomChange={setZoom}
          zoom={zoom}
        />
      </div>
      <div className={styles.preview} ref={setPreviewRef}>
        {loading ? (
          <LoadingSpinner />
        ) : !file ? (
          <div>{intl.formatMessage(messages.notFound)}</div>
        ) : isPdf ? (
          <React.Suspense fallback={<LoadingSpinner />}>
            {blob && <PdfFilePreview className={styles.pdf} file={blob} />}
          </React.Suspense>
        ) : isVideo ? (
          <video className={styles.video} controls>
            <source src={srcUrl} type={file.contentType} />
          </video>
        ) : isImageFile ? (
          <img
            alt={file.name}
            className={styles.image}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            src={srcUrl}
            style={{
              cursor: isPreviewScrollable ? (dragState.isDown ? "grabbing" : "grab") : "default",
              transform: `scale(${zoom})`,
            }}
          />
        ) : (
          <div className={styles.noPreview}>{intl.formatMessage(messages.noPreview)}</div>
        )}
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  onFetchFile: fetchFile,
};

export default connect(null, mapDispatchToProps)(FilePreviewPage);
